import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useToast } from '@contexts/Toast';
import { errorMessage, responseTexts } from '@helpers/Constants';
import {
  AddOperationModalProps,
  ModuleAction,
  ModuleActionTag,
  Modules,
  OperationFormProps,
  Roles,
} from '@helpers/Models';
import { Axios } from '@helpers/ApiService';
import { ApiEndpoints } from '@helpers/Constants/Navigation';
import {
  insertTag,
  getColors,
  contentConversion,
  getAvailableTagsFromList,
} from '@services/OperationService';

export const EditOperationModal: React.FC<AddOperationModalProps> = ({
  isOpen,
  onClose,
  operation,
}) => {
  const { showToastMessage } = useToast();
  const [formData, setFormData] = useState<OperationFormProps>({
    id: 0,
    name: '',
    message: '',
    moduleId: 0,
    moduleActionId: 0,
    operationRoleIds: [],
  });
  const [userRoles, setUserRoles] = useState<Roles[]>([]);
  const [rolePermissions, setRolePermissions] = useState<number[]>([]);
  const [operationMasters, setOperationMasters] = useState<Modules[]>([]);
  const [selectedModule, setSelectedModule] = useState<Modules | null>(null);
  const [selectedAction, setSelectedAction] = useState<ModuleAction | null>(
    null,
  );
  const [message, setMessage] = useState<string>('');
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const editorRef = useRef<HTMLDivElement>(null);
  const [refreshTags, setRefreshTags] = useState(0);

  /**
   * Effect hook that triggers fetching of operation masters when the component/modal is opened.
   *
   * This effect runs whenever `isOpen` changes. If `isOpen` is `true`, it calls `fetchOperationsMasters`
   * to load the necessary operation master data.
   *
   * @param {boolean} isOpen - Determines if the component/modal is open.
   *
   * @effect
   * - Calls `fetchOperationsMasters()` to retrieve and update the operation masters data.
   */
  useEffect(() => {
    if (isOpen) {
      fetchOperationsMasters();
    }
  }, [isOpen]);

  /**
   * Populates form state with the selected operation's data when master data becomes available.
   *
   * This effect runs when `operationMasters` changes. If both `operationMasters` and `operation`
   * are present, it:
   *
   * 1. Sets up the form data fields (id, name, message, module/action IDs, role IDs).
   * 2. Sets the editor message content separately.
   * 3. Finds and sets the selected module based on `operation.moduleId`.
   * 4. Finds and sets the selected action from the selected module’s actions.
   * 5. Sets role permissions using the operation's associated role IDs.
   *
   * This is typically used when editing an existing operation to prefill the form with saved values.
   *
   * Dependencies: [operationMasters]
   */
  useEffect(() => {
    if (operationMasters && operation) {
      setFormData({
        id: operation.id,
        name: operation.name || '',
        message: operation.message || '',
        moduleId: operation.moduleId || 0,
        moduleActionId: operation.moduleActionId ?? 0,
        operationRoleIds: operation.operationRoleIds || [],
      });

      setMessage(operation.message || '');

      const foundModule =
        operationMasters.find((mod) => mod.id === operation.moduleId) || null;
      setSelectedModule(foundModule);

      const foundAction =
        foundModule?.ModuleActions.find(
          (act) => act.id === Number(operation.moduleActionId),
        ) || null;
      setSelectedAction(foundAction);
      setRolePermissions(operation.operationRoleIds || []);
    }
  }, [operationMasters]);

  /**
   * Populates the editor with the operation's message when the modal or drawer is opened.
   *
   * This effect runs when `operation.message` or `isOpen` changes. If the editor is mounted,
   * the operation has a message, and the UI section is open:
   *
   * 1. Splits the `operation.message` into parts, separating plain text and tags using RegEx.
   * 2. For each part:
   *    - If it’s a tag (e.g., [username]), it inserts it using `insertTag()`.
   *    - Otherwise, it appends the plain text directly to the editor DOM.
   * 3. Increments the `refreshTags` counter to trigger any dependent re-renders or effects.
   *
   * Dependencies:
   * - `operation?.message`: Ensures the editor updates when the operation’s message changes.
   * - `isOpen`: Ensures content is only rendered when the editor is visible (e.g., in a modal).
   */
  useEffect(() => {
    if (!editorRef.current || !operation?.message || !isOpen) return;

    // Split the message into parts to isolate tags like [tag]
    const parts = operation.message.split(/(\[.*?\])/g);

    parts.forEach((part) => {
      // Check if the part is a tag by matching the [tag] format
      const tagMatch = part.match(/^\[(.*?)\]$/);
      if (tagMatch) {
        // If it's a tag, wrap it in square brackets again and insert as a tag element
        const tag = `[${tagMatch[1]}]`;
        insertTag(editorRef.current!, tag, undefined);
      } else {
        // If it's plain text, append it as a text node to the editor
        editorRef.current!.appendChild(document.createTextNode(part));
      }
    });
    // Increment refreshTags state to trigger available tags to update
    setRefreshTags((prev) => prev + 1);
  }, [operation?.message, isOpen]);

  /**
   * Asynchronous function to fetch operation masters data from the API and update state.
   *
   * This function makes an HTTP GET request to `ApiEndpoints.FETCH_OPERATIONS_MASTERS`,
   * processes the retrieved data, and updates the state with modules and roles.
   *
   * @async
   * @function fetchOperationsMasters
   * @returns {Promise<void>} - A promise that resolves when data is fetched and state is updated.
   *
   * @throws {Error} - If the request fails, an error message is displayed via `showToastMessage`.
   *
   * @effect
   * - Fetches modules and roles from the API.
   * - Processes the retrieved data to extract relevant properties.
   * - Updates state: `setOperationMasters` with module details and `setUserRoles` with role details.
   *
   */
  const fetchOperationsMasters = async (): Promise<void> => {
    try {
      const response = await Axios.get(ApiEndpoints.FETCH_OPERATIONS_MASTERS);
      const modules = response.data.data.module.map((module: Modules) => ({
        id: module.id,
        name: module.name,
        ModuleActions: module.ModuleActions.map((action: ModuleAction) => ({
          id: action.id,
          name: action.name,
          ModuleActionTags: action.ModuleActionTags.map(
            (tag: ModuleActionTag) => ({
              id: tag.id,
              tag: tag.tag,
            }),
          ),
        })),
      }));
      // Extract `roles` from response
      const roles = response.data.data.roles.map(
        (role: { id: number; name: string }) => ({
          id: Number(role.id),
          name: role.name,
        }),
      );
      setOperationMasters(modules);
      setUserRoles(roles);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Validates the form data and checks for missing required fields.
   *
   * This function verifies if all necessary fields are filled. If a field is empty or invalid,
   * it adds an error message to the `errors` object. After validation, it updates the `setFormErrors` state.
   *
   * @function validateForm
   * @returns {boolean} - Returns `true` if the form is valid (no errors), otherwise `false`.
   *
   * @effect
   * - Checks if `formData.name`, `message`, `selectedModule`, `selectedAction`, and `rolePermissions` are provided.
   * - Populates the `errors` object with validation messages if any required field is missing.
   * - Updates the `setFormErrors` state with detected errors.
   */
  const validateForm = () => {
    const errors: {
      name?: string;
      message?: string;
      module?: string;
      action?: string;
      role?: string;
    } = {};

    if (!message.trim()) {
      errors.message = errorMessage.FIELD_REQUIRED;
    }
    if (!selectedModule) {
      errors.module = errorMessage.FIELD_REQUIRED;
    }
    if (!selectedAction) {
      errors.action = errorMessage.FIELD_REQUIRED;
    }
    if (!rolePermissions || rolePermissions.length === 0) {
      errors.role = errorMessage.FIELD_REQUIRED;
    }
    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /**
   * Handles the form submission for creating or updating an operation.
   *
   * - Prevents default form submission behavior.
   * - Validates the form using `validateForm()`.
   * - Extracts and sanitizes the message content from the editor.
   * - Constructs a payload with the form data and selected role permissions.
   * - Sends a POST request to the `CREATE_OPERATION` endpoint.
   * - Shows a success toast on completion and closes the form/modal.
   * - Displays an error toast if the submission fails.
   *
   * @param {React.FormEvent} event - The form submission event.
   * @returns {Promise<void>} A promise that resolves when the submission process completes.
   */
  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!validateForm()) return;
    const message = contentConversion(editorRef.current?.innerHTML || '');
    try {
      const payload = {
        ...formData,
        message: message,
        operationRoleIds: rolePermissions,
      };

      await Axios.post(ApiEndpoints.CREATE_OPERATION, payload);
      showToastMessage(responseTexts.OPERATION_UPDATE_SUCCESS, 'success');
      onClose();
    } catch (error) {
      showToastMessage(errorMessage.ERROR_UPDATING_OPERATION, 'error');
    }
  };

  /**
   * Handles role selection changes in a checkbox input.
   *
   * This function returns an event handler that updates the `rolePermissions` state
   * when a role checkbox is checked or unchecked.
   *
   * @function handleRolesChange
   * @param {number} roleId - The ID of the role being selected or deselected.
   * @returns {(event: React.ChangeEvent<HTMLInputElement>) => void} -
   *          An event handler function for handling checkbox state changes.
   *
   * @effect
   * - If the checkbox is checked, the `roleId` is added to `rolePermissions`.
   * - If the checkbox is unchecked, the `roleId` is removed from `rolePermissions`.
   * - Logs the current `rolePermissions` state for debugging purposes.
   */
  const handleRolesChange = (roleId: number) => {
    return (event: React.ChangeEvent<HTMLInputElement>): void => {
      if (event.target.checked) {
        setRolePermissions([...rolePermissions, roleId]);
      } else {
        setRolePermissions(rolePermissions.filter((id) => id !== roleId));
      }
    };
  };

  /**
   * Handles changes to the selected module from a dropdown.
   *
   * This function updates the selected module based on user selection, resets the selected action,
   * and updates the form data with the new `moduleId` while resetting `actionId`.
   *
   * @function handleModuleChange
   * @param {React.ChangeEvent<HTMLSelectElement>} event - The change event triggered by selecting a module.
   *
   * @effect
   * - Finds the selected module from `operationMasters` based on the selected value.
   * - Updates the `setSelectedModule` state with the found module or `null` if not found.
   * - Resets the selected action (`setSelectedAction(null)`).
   * - Updates `setFormData` with the selected `moduleId` and resets `actionId` to `0`.
   *
   */
  const handleModuleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const module =
      operationMasters.find((m) => m.id === Number(event.target.value)) || null;
    setSelectedModule(module);
    setSelectedAction(null);
    setFormData((prev) => ({
      ...prev,
      moduleId: module?.id ?? 0,
      actionId: 0,
    }));
  };

  /**
   * Handles changes to the selected action from a dropdown.
   *
   * This function updates the selected action based on user selection and updates
   * the form data with the new `actionId`.
   *
   * @function handleActionChange
   * @param {React.ChangeEvent<HTMLSelectElement>} event - The change event triggered by selecting an action.
   *
   * @effect
   * - Checks if a module is selected (`selectedModule`).
   * - Finds the selected action from `selectedModule.ModuleActions` based on the selected value.
   * - Updates the `setSelectedAction` state with the found action or `null` if not found.
   * - Updates `setFormData` with the selected `actionId` or resets it to `0` if no action is found.
   *
   */
  const handleActionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (selectedModule) {
      const action =
        selectedModule.ModuleActions.find(
          (a) => a.id === Number(event.target.value),
        ) || null;
      setSelectedAction(action);
      setFormData((prev) => ({ ...prev, actionId: action?.id ?? 0 }));
    }
  };

  /**
   * Inserts a tag into the editor content and triggers a tag refresh.
   *
   * If the editor is mounted, this function:
   * - Inserts the specified tag into the current position in the editor using `insertTag`.
   * - Increments the `refreshTags` state to trigger re-rendering or tag-related effects.
   * - The `insertTag` function also optionally accepts a callback, which further increments `refreshTags`.
   *
   * @param {string} tag - The tag string to be inserted (e.g., "[username]").
   */
  const handleTagInsert = (tag: string) => {
    if (editorRef.current) {
      insertTag(editorRef.current, tag, () =>
        setRefreshTags((prev) => prev + 1),
      );
      setRefreshTags((prev) => prev + 1);
    }
  };

  /**
   * Memoized computation of available tags for insertion into the editor.
   *
   * This hook recalculates the list of available tags whenever the selected action
   * changes or the editor content is refreshed (tracked via `refreshTags`).
   *
   * - Returns an empty array if no `selectedAction` is selected or if the editor is not mounted.
   * - Otherwise, it uses `getAvailableTagsFromList` to determine which tags from
   *   `selectedAction.ModuleActionTags` are not yet present in the editor content.
   *
   * Dependencies:
   * - `selectedAction`: Changing the selected action may provide a different set of allowed tags.
   * - `refreshTags`: Triggers recalculation when editor content changes.
   *
   * @returns {string[]} List of tags available for insertion.
   */
  const availableTags = useMemo(() => {
    if (!selectedAction || !editorRef.current) return [];

    return getAvailableTagsFromList(
      editorRef.current,
      selectedAction.ModuleActionTags,
    );
  }, [selectedAction, refreshTags]);

  /**
   * Synchronizes the editor's HTML content with the `message` state.
   *
   * - Retrieves the current HTML content from the editor via `editorRef`.
   * - Cleans the HTML using `contentConversion` to produce a plain text or sanitized version.
   * - Updates the `message` state with the cleaned content.
   *
   * This is typically used to keep the state in sync with manual or programmatic edits to the editor.
   */
  const syncMessageState = () => {
    if (editorRef.current) {
      const cleaned = contentConversion(editorRef.current.innerHTML);
      setMessage(cleaned);
    }
  };

  return isOpen ? (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-white z-10 border-b px-6 py-3">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">Edit Operations</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900"
            >
              ✕
            </button>
          </div>
        </div>
        <div className="overflow-y-auto px-6 py-4 flex-1">
          <form onSubmit={handleSubmit}>
            {/* Module */}
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Modules *{' '}
                  <select
                    value={formData.moduleId}
                    onChange={handleModuleChange}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm  focus:outline-primary"
                  >
                    <option value="">Select Module</option>
                    {operationMasters.map((module) => (
                      <option key={module.id} value={module.id}>
                        {module.name}
                      </option>
                    ))}
                  </select>
                </label>
                {formErrors.module && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.module}
                  </p>
                )}
              </div>

              {/* Action */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Actions *
                  <select
                    value={formData.moduleActionId}
                    onChange={handleActionChange}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm  focus:outline-primary"
                    disabled={!selectedModule}
                  >
                    <option value="">Select Action</option>
                    {selectedModule &&
                      selectedModule.ModuleActions.map((action) => (
                        <option key={action.id} value={action.id}>
                          {action.name}
                        </option>
                      ))}
                  </select>
                  {formErrors.action && (
                    <p className="text-red-500 text-sm mt-1">
                      {formErrors.action}
                    </p>
                  )}
                </label>
              </div>
            </div>

            {/* Mesaage */}
            <span className="mt-4 block text-sm font-medium text-gray-700">
              Message*
            </span>
            <div className="mt-1 border border-gray-300 rounded-lg p-4">
              <div
                ref={editorRef}
                className="w-full min-h-[80px] p-2 rounded border-none focus:outline-none"
                contentEditable
                onInput={syncMessageState}
                onKeyDown={(e) => {
                  // Restricting [] to avoid getting conflicts with [tag]
                  if (e.key === '[' || e.key === ']') {
                    e.preventDefault();
                  }
                }}
              ></div>
              {selectedAction && <hr className="my-4 border-gray-300" />}
              {selectedAction && (
                <div className="flex flex-wrap gap-2 px-2 mb-3 ">
                  {availableTags.map((tag) => {
                    const colors = getColors(tag.tag);
                    return (
                      <button
                        type="button"
                        key={tag.id}
                        onClick={() => handleTagInsert(tag.tag)}
                        className="px-3 py-1 rounded-full text-sm transition"
                        style={{
                          backgroundColor: colors.backgroundColor,
                          border: `1px solid ${colors.borderColor}`,
                          color: colors.textColor,
                        }}
                      >
                        {tag.tag}
                      </button>
                    );
                  })}
                </div>
              )}
              {formErrors.message && (
                <p className="text-red-500 text-sm mt-1">
                  {formErrors.message}
                </p>
              )}
            </div>

            {/* Notification */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                Notification*
                <div className="grid grid-cols-2 gap-2 mt-3 border p-2 pt-4 border-gray-300 rounded-md">
                  {userRoles.map((role: Roles) => (
                    <label
                      key={role.id}
                      className="flex items-center justify-between mr-4 mb-4"
                    >
                      <span className="mr-2 text-sm font-semibold">
                        {role.name}
                      </span>
                      <input
                        type="checkbox"
                        className="w-auto h-auto rounded accent-primary"
                        onChange={handleRolesChange(Number(role.id))}
                        checked={rolePermissions.includes(role.id)}
                      />
                    </label>
                  ))}
                </div>
              </label>
              {formErrors.role && (
                <p className="text-red-500 text-sm mt-1">{formErrors.role}</p>
              )}
            </div>

            {/* Modal Footer Buttons */}
            <div className="mt-6 flex justify-end gap-4">
              <button
                type="button"
                onClick={onClose}
                className="text-gray-600 px-4 py-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 h-10 rounded-md text-white bg-primary"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  ) : null;
};
